import { CURRENCY_SIGN } from '@/constants/countries';

import {
  AccountType,
  AccountTypeDetails,
  AccountTypeStatus,
} from '@/types/api/account';
import { UserAccountType } from '@/types/user';

import { isNil } from '@/utils/common';

import {
  ACCOUNT_TYPE_PERIOD_TEXT,
  SAVINGS_ACCOUNT_PLAIN_FEE_CLASS_CODE,
} from '../constants/account';
import { AccountTypeFeePeriod } from '../types/account';

export function getCurrencySign(currency: string) {
  return CURRENCY_SIGN[currency] || currency;
}

function usesPlainFeeDisplay(accountType: AccountTypeDetails): boolean {
  return (
    accountType.type === AccountType.Savings &&
    accountType.classCode === SAVINGS_ACCOUNT_PLAIN_FEE_CLASS_CODE
  );
}

function getFeeDescription(accountType: AccountTypeDetails): string {
  if (!accountType.feePeriod) return '';
  if (accountType.feePeriod === AccountTypeFeePeriod.Conditional) {
    return accountType.conditionalNote ?? '';
  }
  return ACCOUNT_TYPE_PERIOD_TEXT[accountType.feePeriod] ?? '';
}

export function getAccountHighlightFigure(
  accountType: AccountTypeDetails,
  currentLang: string,
) {
  if (!isNil(accountType.headline)) {
    return {
      figure: accountType.headline,
      figureSubText: '',
      figureDescription: getFeeDescription(accountType),
    };
  }

  if (!isNil(accountType.fee)) {
    const feeValue = Number.parseFloat(
      (accountType.fee || '0').replaceAll(',', ''),
    );
    const plainFeeDisplay = usesPlainFeeDisplay(accountType);
    const figureSubText = plainFeeDisplay
      ? (accountType.feeCurrency ?? '')
      : getCurrencySign(accountType.feeCurrency ?? '');

    const numberFormatOptions: Intl.NumberFormatOptions =
      plainFeeDisplay && feeValue === 0
        ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
        : { minimumFractionDigits: 2 };

    return {
      figure: Intl.NumberFormat(currentLang, numberFormatOptions).format(
        feeValue,
      ),
      figureSubText,
      figureDescription: getFeeDescription(accountType),
    };
  }

  if (accountType.apr) {
    return {
      figure: `${accountType.apr}%`,
      figureDescription: 'APR',
    };
  }

  return {};
}

const ACCOUNT_STATUS_TO_CHECK = new Set([
  AccountTypeStatus.Active,
  AccountTypeStatus.Unfunded,
]);

type GetAccountsWithCheckingLimitArgs = {
  accounts: AccountTypeDetails[];
  userAccountTypes: UserAccountType[];
  selectedAccounts: AccountTypeDetails[];
  maxAccountsPerType?: number;
  requiredSort: boolean;
};

/**
 * Get accounts with checking limit
 * Using user existing accounts and selected accounts to calculate the number of accounts with same classCode
 * @param accounts - Accounts fetch from API
 * @param userAccountTypes - User accounts if from /userInfo [accountTypes]
 * @param selectedAccounts - Selected account from UI
 * @param maxAccountsPerType - Max accounts per type
 * @param requiredSort - Required sort
 * @returns Accounts with checking limit
 */
export function getAccountsWithCheckingLimit({
  accounts,
  userAccountTypes = [],
  selectedAccounts = [],
  maxAccountsPerType = -1,
  requiredSort,
}: GetAccountsWithCheckingLimitArgs) {
  if (maxAccountsPerType < 0) {
    return accounts;
  }

  // Convert user existing accounts to a map { [classCode]: count }
  const accountsForChecking = userAccountTypes.reduce(
    (acc, account: UserAccountType) => {
      // Each account classCode can only be opened due to tenant max accounts
      if (account.id && ACCOUNT_STATUS_TO_CHECK.has(account.status)) {
        // calculate how many accounts with same code are there that user had already opened
        acc[account.id] = (acc[account.id] || 0) + 1;
      }
      return acc;
    },
    {} as Record<string, number>,
  );

  // Convert selected accounts to a map { [classCode]: { count, id } }
  const selectedAccountsForChecking = selectedAccounts.reduce(
    (acc, account: AccountTypeDetails) => {
      // Each account classCode can only be opened due to tenant max accounts
      if (account.id && ACCOUNT_STATUS_TO_CHECK.has(account.status)) {
        // calculate selected accounts for checking and save id for disable check
        const id = String(account.id);
        acc[id] = {
          count: (acc[id]?.count || 0) + 1,
          id: account.id,
        };
      }
      return acc;
    },
    {} as Record<string, { count: number; id: string }>,
  );

  const accountsToShow = accounts.map(account => {
    const id = String(account.id);
    const countOpenedAccounts =
      (accountsForChecking[id] || 0) +
      (selectedAccountsForChecking[id]?.count || 0);
    if (countOpenedAccounts >= maxAccountsPerType) {
      return {
        ...account,
        reachedMaxAccountsPerType:
          selectedAccountsForChecking[id]?.id !== account.id,
      };
    }
    return {
      ...account,
      reachedMaxAccountsPerType: false,
    };
  });

  if (requiredSort) {
    return accountsToShow.sort((a, b) => {
      if (a.reachedMaxAccountsPerType === b.reachedMaxAccountsPerType) {
        return 0;
      }
      if (a.reachedMaxAccountsPerType) {
        return 1;
      }
      return -1;
    });
  }

  return accountsToShow;
}
